import html2canvas from "html2canvas";
import { getCorporationData } from "../../../../services/corporation";
import WasteQualityPDF from "../../../../components/download/wasteQuality/WasteQualityPDF";
import moment from "moment";
import { fetchTemporalData } from "./fetchTemporalData";
import { formatProducerNameWithUniqueId } from "../../characterizationDetails/utils/clientUtils";

const getPdfFilename = (pdfDateTime, corporationName = "") => {
  const pdfFormattedTime = moment(pdfDateTime).format("YYYYMMDD-HH[h]mm");
  return `AK_WasteQualityCarac_${pdfFormattedTime}_${corporationName}.pdf`;
};

/** Capture time for PDF photo pages — newest first. */
const getCaptureTimeMs = (item) => {
  const value = item?._time || item?.time;
  const ms = new Date(value || 0).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const sortByCaptureTimeNewestFirst = (items = []) =>
  [...items].sort((a, b) => getCaptureTimeMs(b) - getCaptureTimeMs(a));

/** scale > 1 pour un rendu PDF plus net (tableaux, graphiques) */
const PDF_CAPTURE_SCALE = 2;

/**
 * Sur le clone utilisé par html2canvas : supprime zoom (Chrome 128+ provoque
 * chevauchement de texte avec zoom), et force box-sizing sur les tableaux.
 */
const sanitizeCloneForCapture = (clonedDoc, clonedElement) => {
  clonedDoc.querySelectorAll("*").forEach((el) => {
    if (el.style && el.style.zoom) el.style.zoom = "";
  });
  clonedElement
    .querySelectorAll(".char-reports-table-container")
    .forEach((el) => {
      if (el.style) el.style.boxSizing = "border-box";
    });
  // Avoid html2canvas clipping scrollable matrix tables on the right/bottom.
  // PDF offscreen capture ([data-pdf-size-matrix]) must stay full-width (no max-content)
  // or the PNG bakes a white strip on the right.
  clonedElement
    .querySelectorAll(".char-reports-matrix-container")
    .forEach((el) => {
      el.style.overflow = "visible";
      el.style.maxWidth = "none";
      el.style.maxHeight = "none";
      if (el.closest("[data-pdf-size-matrix]")) {
        el.style.width = "100%";
        return;
      }
      el.style.width = "max-content";
    });
  clonedElement.querySelectorAll(".char-reports-matrix-table").forEach((el) => {
    if (el.closest("[data-pdf-size-matrix]")) {
      el.style.width = "100%";
      el.style.minWidth = "100%";
      el.style.tableLayout = "fixed";
      return;
    }
    el.style.minWidth = "max-content";
    el.style.width = "auto";
  });
};

const captureCanvasData = async (element) => {
  // Tableaux réglementaire / filières / REP : capture comme avant 72476735.
  // windowWidth = largeur élément (~600–800px) activait @media (max-width: 950px)
  // → flex-direction: column → lignes empilées + troncature dans le PDF.
  const isReportsTable = Boolean(
    element?.classList?.contains("char-reports-table-container"),
  );
  if (isReportsTable) {
    return html2canvas(element, {
      allowTaint: true,
      scale: PDF_CAPTURE_SCALE,
      useCORS: true,
      logging: false,
      onclone: sanitizeCloneForCapture,
    });
  }

  // Charts + matrice de tailles : dimensions explicites (fix crop matrice, 72476735).
  const width = Math.ceil(element.scrollWidth || element.offsetWidth || 0);
  const height = Math.ceil(element.scrollHeight || element.offsetHeight || 0);
  return html2canvas(element, {
    allowTaint: true,
    scale: PDF_CAPTURE_SCALE,
    useCORS: true,
    logging: false,
    width: width || undefined,
    height: height || undefined,
    windowWidth: width || undefined,
    windowHeight: height || undefined,
    onclone: sanitizeCloneForCapture,
  });
};

export const captureCharacterizationPdf = async ({
  refs,
  period,
  superContainer,
  container,
  historyMaterial,
  characterizationType,
  sizeThreshold = 40,
  totalWasteMass,
  totalMassRecoverable,
  totalWasteVolume,
  totalVolumeRecoverable,
  totalWeight,
  client,
  registrationClientCode,
  wasteCategoryName,
  parentCategoriesList,
  comments = [],
  t,
  reportOptions = {
    mass: true,
    volume: true,
    outlet: true,
    comments: false,
    sizeDistribution: false,
  },
}) => {
  const {
    massChartRef,
    massTableRef,
    outletChartRef,
    outletTableRef,
    volumeChartRef,
    objectsChartRef,
    repChartRef,
    repTableRef,
    sizeChartRef,
    sizeMatrixRef,
  } = refs;
  const pdfDateTime = new Date(period?.start || period?._time || new Date());
  const user = localStorage.getItem("user");

  const fetchTemporalDataForPdf = async () => {
    const historyNewestFirst = sortByCaptureTimeNewestFirst(historyMaterial);
    try {
      if (superContainer && superContainer._id) {
        const ids = Array.isArray(superContainer.containerObjectIdList)
          ? superContainer.containerObjectIdList.filter(Boolean)
          : [];
        const result = await Promise.all(
          ids.map((containerId) =>
            Promise.all(
              historyNewestFirst.map((item) =>
                fetchTemporalData(item, containerId, t),
              ),
            ).then(sortByCaptureTimeNewestFirst),
          ),
        );
        return result.length === 0 ? [[], []] : result;
      }
      const temporalData = await Promise.all(
        historyNewestFirst.map((item) =>
          fetchTemporalData(item, container?._id, t),
        ),
      );
      return sortByCaptureTimeNewestFirst(temporalData);
    } catch (error) {
      console.error("Error fetching temporal data for PDF:", error);
      return superContainer && superContainer._id ? [[], []] : [];
    }
  };

  const shouldCaptureSizeDistribution = Boolean(reportOptions.sizeDistribution);
  const capturePromises = [
    massChartRef?.current
      ? captureCanvasData(massChartRef.current)
      : Promise.resolve(null),
    massTableRef?.current
      ? captureCanvasData(massTableRef.current)
      : Promise.resolve(null),
    outletChartRef?.current
      ? captureCanvasData(outletChartRef.current)
      : Promise.resolve(null),
    outletTableRef?.current
      ? captureCanvasData(outletTableRef.current)
      : Promise.resolve(null),
    volumeChartRef?.current
      ? captureCanvasData(volumeChartRef.current)
      : Promise.resolve(null),
    objectsChartRef?.current
      ? captureCanvasData(objectsChartRef.current)
      : Promise.resolve(null),
    repChartRef?.current
      ? captureCanvasData(repChartRef.current)
      : Promise.resolve(null),
    repTableRef?.current
      ? captureCanvasData(repTableRef.current)
      : Promise.resolve(null),
    shouldCaptureSizeDistribution && sizeChartRef?.current
      ? captureCanvasData(sizeChartRef.current)
      : Promise.resolve(null),
    shouldCaptureSizeDistribution && sizeMatrixRef?.current
      ? captureCanvasData(sizeMatrixRef.current)
      : Promise.resolve(null),
  ];

  const [responseCorporation, ...captures] = await Promise.all([
    getCorporationData(user),
    ...capturePromises,
    fetchTemporalDataForPdf(),
  ]);

  let corporationData = "";
  if (responseCorporation?.status >= 200 && responseCorporation?.status < 400) {
    corporationData = responseCorporation.data.corporation;
  }

  const temporalDataArray = captures.pop();
  const [
    chartImgData,
    tableImgData,
    outletImgData,
    tableOutletImgData,
    volumeImgData,
    materialImgData,
    repChartImgData,
    tableRepImgData,
    sizeChartImgData,
    sizeMatrixImgData,
  ] = captures.map((canvas) => (canvas ? canvas.toDataURL("image/png") : null));
  const pdfContent = (
    <WasteQualityPDF
      corporationData={corporationData}
      caracDatetime={pdfDateTime}
      totalWasteMass={totalWasteMass}
      totalMassRecoverable={totalMassRecoverable}
      chartImageUrl={chartImgData}
      tableImageUrl={tableImgData}
      materialImageUrl={materialImgData}
      selectedNumberOfCycleMaterial={
        Array.isArray(historyMaterial) ? historyMaterial.length : 0
      }
      volumeImgData={volumeImgData}
      outletImgData={outletImgData}
      tableOutletImgData={tableOutletImgData}
      sizeChartImgData={sizeChartImgData}
      sizeMatrixImgData={sizeMatrixImgData}
      totalWasteVolume={totalWasteVolume}
      totalVolumeRecoverable={totalVolumeRecoverable}
      temporalDataArray={temporalDataArray}
      container={container}
      totalWeight={totalWeight ? totalWeight : 0}
      clientName={formatProducerNameWithUniqueId(client)}
      clientCode={registrationClientCode ?? ""}
      clientAddress={client?.address || ""}
      superContainer={!!(superContainer && superContainer._id)}
      wasteCategoryName={wasteCategoryName || ""}
      parentCategoriesList={parentCategoriesList || []}
      characterizationOutlet={reportOptions.outlet}
      comments={comments}
      chartRepImgData={repChartImgData}
      tableRepImgData={tableRepImgData}
      characterizationType={characterizationType || null}
      characterizationComments={reportOptions.comments}
      repDescriptionDatas={[]}
      categoryMassOverLimit={[]}
      characterizationVolumeAnalysis={reportOptions.volume}
      characterizationMassAnalysis={reportOptions.mass}
      characterizationSizeDistribution={reportOptions.sizeDistribution}
      sizeThreshold={sizeThreshold}
    />
  );
  const pdfFilename = getPdfFilename(
    pdfDateTime,
    corporationData?.corporationName ?? "",
  );
  return { pdfContent, pdfFilename, pdfDateTime };
};
